import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { config } from "./config";
import { initSentry } from "./config/sentry";

/**
 * API Configuration Note (2025-11-06):
 * - Production: https://optimizer-api.ai-edge.io
 * - Staging: https://staging-optimizer-api.ai-edge.io (fixed from incorrect staging-api.optimizer.ai-edge.io)
 * Environment variables are managed via 1Password and synced to Vercel
 * Last deployment trigger: 2025-11-11
 */
import {
  setupGlobalErrorHandling,
  logPerformanceMetrics,
  monitorResourceErrors,
} from "./utils/globalErrorHandler";
import { logger } from "./utils/logger";
import {
  setupErrorInterceptor,
  interceptAPIErrors,
} from "./utils/errorInterceptor";

// Initialize error handling and monitoring (essential for production debugging)
try {
  initSentry();
  setupGlobalErrorHandling();
  logPerformanceMetrics();
  monitorResourceErrors();
  setupErrorInterceptor();
  interceptAPIErrors();
} catch (error) {
  console.error("Error during initialization setup:", error);
  // Continue without these features rather than breaking the entire app
}

logger.info("🚀 Application starting", {
  timestamp: new Date().toISOString(),
  environment: import.meta.env.MODE,
  version: import.meta.env.VITE_APP_VERSION || "unknown",
  features: config.features,
  sourceMapEnabled: true,
});

// Import debug utilities when enabled
if (config.features.debugMode) {
  import("./utils/debug");
  import("./utils/authDebugger");
}

// Robust React root creation with error handling
const rootElement = document.getElementById("root");
if (rootElement) {
  try {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  } catch (error) {
    console.error("Error creating or rendering React app:", error);
    logger.error("Failed to render React app", error);
    // Fallback to error display
    rootElement.innerHTML = `
      <div style="color: white; padding: 20px; font-family: monospace;">
        <h1>Application Error</h1>
        <p>The application failed to start. Please check the console for details.</p>
        <p><strong>Error:</strong> ${error}</p>
        <p><strong>Environment:</strong> ${import.meta.env.MODE}</p>
        <p>Refresh the page to try again.</p>
      </div>
    `;
  }
} else {
  console.error("Root element not found!");
}
